import React from 'react'
import { useSnapshot } from 'valtio'
import { openURL } from 'renderer/viewer/lib/simpleUtils'
import {
  UTOPIA_BEDROCK_HOST,
  UTOPIA_BEDROCK_PORT,
  UTOPIA_SERVER_HOST,
  UTOPIA_SITE_URL,
} from '../../utopia/constants'
import { openUtopiaCguModal } from './utopiaOverlayState'
import { getUtopiaPlayButtonState, utopiaServerStatusState } from './utopiaServerStatus'
import styles from './utopiaLanding.module.css'

type Props = {
  onPlayClick: () => void
  onOpenAuth: () => void
}

function LoginCmd () {
  return <code className={styles.joinCode}>/login &lt;votre mot de passe&gt;</code>
}

export default function UtopiaJoin ({ onPlayClick, onOpenAuth }: Props) {
  useSnapshot(utopiaServerStatusState)
  const playBrowser = getUtopiaPlayButtonState('browser')

  const handlePlayClick = () => {
    if (playBrowser.disabled) return
    onPlayClick()
  }

  return (
    <div className={`${styles.pagePanel} ${styles.joinPage}`}>
      <header className={styles.joinHeader}>
        <h2 className={styles.joinTitle}>Rejoindre Utopie</h2>
        <p className={styles.joinIntro}>
          Le serveur Utopie est accessible en Minecraft Java et Bedrock (consoles Xbox, PlayStation,
          Switch, PC), ainsi que depuis votre navigateur web, sans installation.
        </p>
      </header>

      <section className={styles.joinSection}>
        <h3 className={styles.joinSectionTitle}>Version Web (dans votre navigateur)</h3>
        <div className={styles.joinCard}>
          <p className={styles.joinCardDesc}>
            Jouez instantanément sans installer Minecraft : le client fonctionne directement depuis ce site.
          </p>
          <ol className={styles.joinSteps}>
            <li>
              Ouvrez{' '}
              <button
                type="button"
                className={styles.joinLink}
                onClick={() => openURL(UTOPIA_SITE_URL, true)}
              >
                utopia.csphere.fr
              </button>.
            </li>
            <li>
              Cliquez sur <button type="button" className={styles.joinLink} onClick={onOpenAuth}>Connexion</button>
              {' '}en haut à droite, puis créez votre compte Utopie ou connectez-vous.
              <span className={styles.joinStepsNote}>
                {' '}Vous devrez renseigner : une adresse e-mail, un mot de passe et votre pseudo Minecraft.
              </span>
            </li>
            <li>Depuis l&apos;accueil, cliquez sur <strong>Jouer dans le navigateur</strong>.</li>
            <li>
              Une fois en jeu, ouvrez le chat (touche <strong>T</strong> par défaut) puis connectez-vous avec la commande :{' '}
              <LoginCmd />
            </li>
          </ol>
          <button
            type="button"
            className={styles.playCta}
            onClick={handlePlayClick}
            disabled={playBrowser.disabled}
            title={playBrowser.disabled ? playBrowser.label : undefined}
          >
            ▶ {playBrowser.label}
          </button>
        </div>
      </section>

      <section className={styles.joinSection}>
        <h3 className={styles.joinSectionTitle}>Version officielle Minecraft Java</h3>
        <div className={styles.joinCard}>
          <p className={styles.joinCardDesc}>
            Pour les joueurs possédant un compte Mojang / Microsoft avec une licence Minecraft Java officielle.
          </p>
          <ol className={styles.joinSteps}>
            <li>
              Ouvrez{' '}
              <button type="button" className={styles.joinLink} onClick={() => openURL(UTOPIA_SITE_URL, true)}>
                utopia.csphere.fr
              </button>
              {' '}&amp; connectez-vous avec votre compte Microsoft afin de créer et associer votre compte Utopie.
            </li>
            <li>Lancez le launcher officiel Minecraft Java.</li>
            <li>
              Connectez-vous avec le compte Microsoft correspondant au pseudo utilisé lors de la création de votre compte Utopie.
            </li>
            <li>Dans le menu du jeu, allez dans <strong>Multijoueur</strong> → <strong>Ajouter un serveur</strong> (ou Connexion directe).</li>
            <li>
              Saisissez l&apos;adresse du serveur : <strong className={styles.joinHost}>{UTOPIA_SERVER_HOST}</strong>
            </li>
            <li>
              Rejoignez le serveur puis connectez-vous avec : <LoginCmd />
            </li>
          </ol>
        </div>
      </section>

      <section className={styles.joinSection}>
        <h3 className={styles.joinSectionTitle}>Minecraft Bedrock (console)</h3>
        <div className={styles.joinCard}>
          <p className={styles.joinCardDesc}>
            Xbox, PlayStation ou Switch. L&apos;inscription se fait avec un code affiché en jeu
            — pas besoin de licence Java.
          </p>
          <ol className={styles.joinSteps}>
            <li>
              Dans Minecraft <strong>Bedrock</strong>, allez dans <strong>Jouer</strong>
              {' '}→ <strong>Serveurs</strong> → <strong>Ajouter un serveur</strong>.
            </li>
            <li>
              Adresse : <strong className={styles.joinHost}>{UTOPIA_BEDROCK_HOST}</strong>
              {' '}— Port : <strong className={styles.joinHost}>{UTOPIA_BEDROCK_PORT}</strong>
            </li>
            <li>
              Rejoignez le serveur. Un message affiche un <strong>code à 8 caractères</strong>
              {' '}(valide 15 minutes) et votre plateforme (Xbox, PlayStation, etc.).
            </li>
            <li>
              Sur le <strong>même réseau Internet</strong> (même box que la console),
              ouvrez{' '}
              <button type="button" className={styles.joinLink} onClick={() => openURL(UTOPIA_SITE_URL, true)}>
                utopia.csphere.fr
              </button>
              {' '}→ <button type="button" className={styles.joinLink} onClick={onOpenAuth}>Connexion</button>
              {' '}→ <strong>S&apos;inscrire</strong> → <strong>Inscription Bedrock Console</strong>.
            </li>
            <li>
              Saisissez le code reçu en jeu : votre pseudo Bedrock est détecté automatiquement
              {' '}(avec le <strong>.</strong> au début si le serveur l&apos;ajoute, ex. <code className={styles.joinCode}>.MonPseudo</code>).
            </li>
            <li>
              Choisissez un mot de passe, acceptez les CGU, puis créez le compte.
            </li>
            <li>
              Retournez en Bedrock sur le serveur et connectez-vous avec : <LoginCmd />
            </li>
          </ol>
          <p className={styles.joinStepsNote}>
            Mobile Android / iOS : l&apos;inscription par code n&apos;est pas disponible ;
            utilisez la version web ou un compte Microsoft sur le site si vous possédez Minecraft Java.
          </p>
        </div>
      </section>

      <section className={styles.joinSection}>
        <h3 className={styles.joinSectionTitle}>Autres launchers (TLauncher, Prism, Lunar, etc.)</h3>
        <div className={styles.joinCard}>
          <p className={styles.joinCardDesc}>
            Vous pouvez également rejoindre Utopie avec la plupart des launchers Minecraft alternatifs.
          </p>
          <ol className={styles.joinSteps}>
            <li>
              Créez d&apos;abord votre compte Utopie sur :{' '}
              <button type="button" className={styles.joinLink} onClick={() => openURL(UTOPIA_SITE_URL, true)}>
                utopia.csphere.fr
              </button>
            </li>
            <li>
              Lors de l&apos;inscription, utilisez exactement le même pseudo que celui configuré sur votre launcher Minecraft.
            </li>
            <li>
              Lancez Minecraft puis ajoutez le serveur : <strong className={styles.joinHost}>{UTOPIA_SERVER_HOST}</strong>
            </li>
            <li>
              Une fois connecté en jeu, ouvrez le chat avec la touche <strong>T</strong> puis entrez : <LoginCmd />
            </li>
          </ol>
        </div>
      </section>

      <section className={styles.joinSection}>
        <h3 className={styles.joinSectionTitle}>Connexion automatique (Session IP)</h3>
        <div className={styles.joinCard}>
          <p className={styles.joinCardDesc}>
            Option pour ne pas retaper <LoginCmd /> à chaque fois, depuis le même réseau (même box Internet).
          </p>
          <ol className={styles.joinSteps}>
            <li>
              Connectez-vous sur le site via{' '}
              <button type="button" className={styles.joinLink} onClick={onOpenAuth}>Connexion</button>
              {' '}(en haut à droite).
            </li>
            <li>
              Dans le menu compte, cliquez sur <strong>Session IP</strong> (option).
            </li>
            <li>
              Appuyez sur <strong>Activer maintenant</strong> depuis l&apos;appareil / le réseau avec lequel vous jouerez.
            </li>
            <li>
              Rejoignez le serveur : la connexion Utopie se fera automatiquement si votre IP correspond.
              Sinon, utilisez encore <LoginCmd />.
            </li>
          </ol>
        </div>
      </section>

      <section className={styles.joinImportant}>
        <h3 className={styles.joinSectionTitle}>Important</h3>
        <ul className={styles.joinBulletList}>
          <li>
            Un compte <strong>Mojang / Microsoft</strong> avec Minecraft Java est recommandé pour l&apos;inscription classique ;
            les joueurs <strong>Bedrock console</strong> peuvent utiliser l&apos;inscription par code (voir ci-dessus).
          </li>
          <li>Le pseudo utilisé sur le site Utopie doit être identique à celui utilisé en jeu.</li>
          <li>
            Conservez votre mot de passe : il vous sera demandé avec la commande : <LoginCmd />
            {' '}sauf si vous avez activé la <strong>Session IP</strong>.
          </li>
          <li>Le serveur est entièrement gratuit et accessible sur la majorité des appareils.</li>
        </ul>
        <button
          type="button"
          className={styles.joinCguToggle}
          onClick={openUtopiaCguModal}
        >
          Afficher les conditions générales d&apos;utilisation (CGU)
        </button>
      </section>
    </div>
  )
}
